import { groupBy } from 'ramda'
import { keys } from 'ramda'
import React, { UIEvent } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useUserState } from '~/atoms/UserState'
import Text, { FontStyle } from '~/components/core/Text'
import { spacing } from '~/constants/theme'
import { PendingMessage } from '~/pages/Messaging/hooks/usePendingMessages'
import { desktop } from '~/styles'
import { formatRelativeDay } from '~/utils/formatter'

import { formatTimeString } from '@mangovoice/core'
import { CallLogDirection, Message } from '@mangovoice/types'

import MessageBubble from './MessageBubble'
import MessageDateSplitter from './MessageDateSplitter'
import PendingMessageBubble from './PendingMessageBubble'

// column-reverse keeps the view anchored to the newest message (and keeps the
// scroll position when older pages load above) while the single child below
// holds the messages in chronological DOM order for assistive tech.
const RootWrapper = styled.div`
  width: 100%;
  overflow-y: scroll;
  display: flex;
  flex-direction: column-reverse;

  &::-webkit-scrollbar {
    width: ${desktop.scrollbar}px;
    background-color: transparent;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: ${desktop.scrollbar}px;
    -webkit-box-shadow: inset 0px 4px 14px rgba(0, 0, 0, 0.1);
    background-color: var(--chakra-colors-border-default);
  }
`

const ChronologicalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const MessageEndWrapper = styled.div`
  float: left;
  clear: both;
`

const MessageGroupWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const MessageItemWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: ${spacing.XS} 0;
  box-sizing: border-box;
`

const VisuallyHiddenText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`

interface MessageTimeWrapperProps {
  $self?: boolean
}
const MessageTimeWrapper = styled.div<MessageTimeWrapperProps>`
  width: 100%;
  height: 22px;
  padding-top: ${spacing.spacingLG};
  padding-bottom: ${spacing.spacingSM};
  display: flex;
  align-items: flex-end;
  justify-content: ${(props) => (props.$self ? 'flex-end' : 'flex-start')};

  > p:nth-child(2) {
    margin-left: 4px;
  }
`

function isPendingMessage(
  value: Message | PendingMessage,
): value is PendingMessage {
  // Currently Message doesn't have a uuid and PendingMessage always does, so check if it has a uuid after casting
  return (value as PendingMessage).uuid !== undefined
}

interface MessageListProps {
  /** Newest first, as returned by the API. */
  messages: (Message | PendingMessage)[]
  /** Name of the external party, used to attribute inbound messages. */
  contactName?: string
  onScroll?: (e: UIEvent<HTMLDivElement>) => void
  onRetrySend?: (message: PendingMessage) => void
}
const MessageList = React.forwardRef<HTMLInputElement, MessageListProps>(
  (props, ref) => {
    const { messages, contactName, onScroll, onRetrySend } = props
    const { t } = useTranslation()
    const { pbxUsers, sessionUser } = useUserState()

    const chronologicalMessages = [...messages].reverse()
    const dateGroupedMessages = groupBy(
      (x) => formatRelativeDay(x.created_at),
      chronologicalMessages,
    )

    return (
      <RootWrapper onScroll={onScroll}>
        <ChronologicalContent>
          {keys(dateGroupedMessages).map((dateKey) => {
            const messageList = dateGroupedMessages[dateKey]
            return (
              <div key={`message-group-${String(dateKey)}`}>
                <MessageDateSplitter date={dateKey} />
                <MessageGroupWrapper>
                  {messageList?.map((message, ind) => {
                    const { direction, sender_user_id: messageUserId } = message

                    const messageUserName =
                      pbxUsers?.find((user) => user.id === messageUserId)
                        ?.full_name ?? ''

                    const isPending = isPendingMessage(message)
                    const fromCurrentUser =
                      isPending || messageUserId === sessionUser?.id

                    const isFromTeam = direction === CallLogDirection.Outbound

                    const messageTime = formatTimeString(message.created_at, {
                      hour12: true,
                    })
                    let showTime = true
                    const previousMsg = messageList[ind - 1]
                    if (previousMsg) {
                      const previousTime = formatTimeString(
                        previousMsg.created_at,
                        { hour12: true },
                      )
                      if (
                        message.sender_user_id === previousMsg.sender_user_id &&
                        messageTime === previousTime
                      )
                        showTime = false
                    }

                    let senderName: string
                    if (!isFromTeam) {
                      senderName =
                        contactName || t('a11y.messages.senderContact')
                    } else if (fromCurrentUser) {
                      senderName = t('a11y.messages.senderYou')
                    } else {
                      senderName =
                        messageUserName || t('a11y.messages.senderTeam')
                    }

                    const key = isPending
                      ? `pending-${message.uuid}`
                      : message.id

                    return (
                      <MessageItemWrapper key={`message-${key}`}>
                        <VisuallyHiddenText>
                          {t('a11y.messages.messageMeta', {
                            sender: senderName,
                            time: messageTime,
                          })}
                        </VisuallyHiddenText>
                        {showTime && (
                          <MessageTimeWrapper $self={isFromTeam} aria-hidden>
                            {isFromTeam && (
                              <Text
                                fontStyle={FontStyle.bSmall}
                                text={messageUserName}
                                color="var(--chakra-colors-fg-muted)"
                              />
                            )}
                            <Text
                              fontStyle={FontStyle.Tiny}
                              text={messageTime}
                              color="var(--chakra-colors-fg-muted)"
                            />
                          </MessageTimeWrapper>
                        )}

                        {isPending ? (
                          <PendingMessageBubble
                            message={message}
                            onRetry={onRetrySend}
                          />
                        ) : (
                          <MessageBubble
                            fromCurrentUser={fromCurrentUser}
                            isFromTeam={isFromTeam}
                            message={message}
                            senderName={senderName}
                          />
                        )}
                      </MessageItemWrapper>
                    )
                  })}
                </MessageGroupWrapper>
              </div>
            )
          })}
          <MessageEndWrapper ref={ref} />
        </ChronologicalContent>
      </RootWrapper>
    )
  },
)

export default MessageList
